import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { FirePanel, FirePanelSecret } from '@cloudsign/shared';
import type { z } from 'zod';

type Secret = z.infer<typeof FirePanelSecret>;
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useSite } from '../../lib/site';
import { Badge, Button, Card, CheckboxField, ConfirmButton, EmptyRow, PageHeader, SelectField, Spinner, TableWrap, Th } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { SecretDialog } from '../../components/phase2';
import { evacKey, useFirePanels } from './api';

interface FormState {
  name: string;
  siteId: string;
  mode: 'signed' | 'token';
  cidrs: string;
  canEndAny: boolean;
}

/** One address or range per line (commas also work). */
export function parseCidrs(text: string): string[] {
  return text
    .split(/[\n,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function PanelForm({ panel, siteId, onDone, onCancel }: { panel: FirePanel | null; siteId: string; onDone: (secret: Secret | null) => void; onCancel: () => void }) {
  const qc = useQueryClient();
  const { sites } = useSite();
  const [f, setF] = useState<FormState>({
    name: panel?.name ?? '',
    siteId: panel?.siteId ?? siteId,
    mode: panel?.mode ?? 'signed',
    cidrs: (panel?.allowedCidrs ?? []).join('\n'),
    canEndAny: panel?.canEndAny ?? false,
  });
  const cidrs = parseCidrs(f.cidrs);
  const needsCidrs = f.mode === 'token' && cidrs.length === 0;
  const save = useMutation({
    mutationFn: async () => {
      if (panel) {
        await api.patch(`/evacuation/fire-panels/${panel.id}`, { body: { name: f.name.trim(), allowedCidrs: cidrs, canEndAny: f.canEndAny }, schema: FirePanel });
        return null;
      }
      return api.post('/evacuation/fire-panels', { body: { siteId: f.siteId, name: f.name.trim(), mode: f.mode, allowedCidrs: cidrs, canEndAny: f.canEndAny }, schema: FirePanelSecret });
    },
    onSuccess: async (secret) => {
      await qc.invalidateQueries({ queryKey: [...evacKey, 'panels'] });
      onDone(secret);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (f.name.trim() && f.siteId && !needsCidrs) save.mutate();
  };
  return (
    <form onSubmit={submit} aria-label={panel ? t('evac.panels.edit') : t('evac.panels.new')}>
      <Card
        title={panel ? panel.name : t('evac.panels.new')}
        footer={
          <>
            <Button onClick={onCancel}>{t('common.cancel')}</Button>
            <Button type="submit" variant="primary" disabled={save.isPending || !f.name.trim() || !f.siteId || needsCidrs}>
              {panel ? t('evac.panels.save') : t('evac.panels.create')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <ErrorLine error={save.error} />
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm font-medium text-slate-800">
              {t('evac.panels.name')}
              <input required maxLength={80} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 block min-h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-normal sm:min-h-9" />
            </label>
            <SelectField label={t('evac.panels.site')} disabled={panel !== null} value={f.siteId} onChange={(e) => setF({ ...f, siteId: e.target.value })}>
              {sites
                .filter((s) => !s.isRemote)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </SelectField>
          </div>
          <SelectField label={t('evac.panels.col.mode')} hint={t('evac.panels.modeHint')} disabled={panel !== null} value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value as FormState['mode'] })}>
            <option value="signed">{t('evac.panels.mode.signed')}</option>
            <option value="token">{t('evac.panels.mode.token')}</option>
          </SelectField>
          <div>
            <label htmlFor="evac-panel-cidrs" className="mb-1 block text-sm font-medium text-slate-800">
              {t('evac.panels.cidrs')}
            </label>
            <textarea
              id="evac-panel-cidrs"
              rows={3}
              value={f.cidrs}
              onChange={(e) => setF({ ...f, cidrs: e.target.value })}
              aria-invalid={needsCidrs || undefined}
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 font-mono text-sm"
            />
            <p className={`mt-1 text-xs ${needsCidrs ? 'text-red-700' : 'text-slate-600'}`}>{needsCidrs ? t('evac.panels.cidrsNeeded') : t('evac.panels.cidrsHint')}</p>
          </div>
          <CheckboxField label={t('evac.panels.canEndAny')} hint={t('evac.panels.canEndAnyHint')} checked={f.canEndAny} onChange={(e) => setF({ ...f, canEndAny: e.target.checked })} />
        </div>
      </Card>
    </form>
  );
}

/** Fire panels under Manage: list, create (the secret is shown once), edit, enable, replace the secret, delete. */
export function FirePanelsPage() {
  const qc = useQueryClient();
  const { sites, siteId } = useSite();
  const q = useFirePanels();
  const [mode, setMode] = useState<'new' | string | null>(null);
  const [secret, setSecret] = useState<Secret | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: [...evacKey, 'panels'] });
  const toggle = useMutation({
    mutationFn: (p: FirePanel) => api.patch(`/evacuation/fire-panels/${p.id}`, { body: { enabled: !p.enabled }, schema: FirePanel }),
    onSuccess: refresh,
  });
  const rotate = useMutation({
    mutationFn: (p: FirePanel) => api.post(`/evacuation/fire-panels/${p.id}/rotate`, { schema: FirePanelSecret }),
    onSuccess: async (s) => {
      setSecret(s);
      await refresh();
    },
  });
  const remove = useMutation({ mutationFn: (p: FirePanel) => api.delete(`/evacuation/fire-panels/${p.id}`), onSuccess: refresh });
  const siteName = (id: string) => sites.find((s) => s.id === id)?.name ?? '';
  const first = sites.find((s) => !s.isRemote)?.id ?? '';

  return (
    <div>
      <PageHeader
        title={t('evac.panels.title')}
        description={t('evac.panels.description')}
        actions={
          mode === null ? (
            <Button variant="primary" onClick={() => setMode('new')}>
              <Plus aria-hidden="true" className="h-4 w-4" />
              {t('evac.panels.new')}
            </Button>
          ) : null
        }
      />
      <div className="space-y-4">
        <ErrorLine error={toggle.error ?? rotate.error ?? remove.error} />
        {mode === 'new' ? (
          <PanelForm
            panel={null}
            siteId={siteId ?? first}
            onCancel={() => setMode(null)}
            onDone={(s) => {
              setMode(null);
              setSecret(s);
            }}
          />
        ) : null}
        {q.isError ? <ErrorPanel error={q.error} onRetry={() => void q.refetch()} /> : null}
        {q.isLoading ? <Spinner /> : null}
        {q.data ? (
          <TableWrap caption={t('evac.panels.table')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('evac.panels.col.name')}</Th>
                <Th>{t('evac.panels.col.site')}</Th>
                <Th>{t('evac.panels.col.mode')}</Th>
                <Th>{t('evac.panels.col.lastCall')}</Th>
                <Th>{t('evac.panels.col.status')}</Th>
                <Th>{t('evac.panels.col.actions')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {q.data.length === 0 ? <EmptyRow cols={6} /> : null}
              {q.data.map((p) =>
                mode === p.id ? (
                  <tr key={p.id}>
                    <td colSpan={6} className="p-3">
                      <PanelForm
                        panel={p}
                        siteId={p.siteId}
                        onCancel={() => setMode(null)}
                        onDone={() => setMode(null)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={p.id}>
                    <td className="px-4 py-3 font-medium">
                      {p.name}
                      <p className="break-all font-mono text-xs font-normal text-slate-600">
                        {t('evac.panels.url')}: {p.url}
                      </p>
                    </td>
                    <td className="px-4 py-3">{siteName(p.siteId)}</td>
                    <td className="px-4 py-3">{t(`evac.panels.mode.${p.mode}`)}</td>
                    <td className="px-4 py-3">
                      {p.lastCallAt ? (
                        <>
                          {formatDateTime(p.lastCallAt)}
                          {p.lastCallResult ? <p className="text-xs text-slate-600">{t('evac.panels.lastResult', { result: p.lastCallResult })}</p> : null}
                        </>
                      ) : (
                        t('evac.panels.never')
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={p.enabled ? 'good' : 'neutral'}>{p.enabled ? t('evac.panels.enabled') : t('evac.panels.disabled')}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Button onClick={() => setMode(p.id)}>{t('evac.panels.edit')}</Button>
                        <Button disabled={toggle.isPending} onClick={() => toggle.mutate(p)}>
                          {p.enabled ? t('evac.panels.disable') : t('evac.panels.enable')}
                        </Button>
                        <ConfirmButton label={t('evac.panels.rotate')} confirmLabel={t('evac.panels.rotateConfirm')} pending={rotate.isPending} onConfirm={() => rotate.mutate(p)} />
                        <ConfirmButton label={t('evac.panels.delete')} confirmLabel={t('evac.panels.deleteConfirm')} pending={remove.isPending} onConfirm={() => remove.mutate(p)} />
                      </div>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </TableWrap>
        ) : null}
        <p className="text-sm text-slate-600">{t('evac.panels.testNote')}</p>
      </div>
      {secret ? (
        <SecretDialog
          title={t('evac.panels.secretTitle', { name: secret.panel.name })}
          description={t('evac.panels.secretDescription')}
          secret={secret.secret}
          secretLabel={t('evac.panels.secretLabel')}
          onClose={() => setSecret(null)}
        />
      ) : null}
    </div>
  );
}
